// pcd-checkout-guard.js
// Intercepts the Shopify checkout button and uploads pending design images
// to the CDN before the order is placed, then updates cart line item properties.
//
// Design data (2-5 MB PNG data URLs) are stored in IndexedDB by product-customizer.js.
// The upload endpoint URL is stored in localStorage (it's tiny).

(function () {
  'use strict';

  const ENDPOINT_KEY   = 'pcd_upload_endpoint';
  const DESIGNS_PREFIX = 'pcd_designs_';
  const LOG            = '[PCD guard]';

  // ── IndexedDB helpers (mirrors product-customizer.js) ────────────────────────
  const _PCD_DB    = 'pcd-designs';
  const _PCD_STORE = 'designs';

  function _pcdOpenDB() {
    return new Promise((res, rej) => {
      const r = indexedDB.open(_PCD_DB, 1);
      r.onupgradeneeded = e => e.target.result.createObjectStore(_PCD_STORE);
      r.onsuccess = e => res(e.target.result);
      r.onerror   = e => rej(e.target.error);
    });
  }

  function pcdIdbGet(key) {
    return _pcdOpenDB().then(db => new Promise((res, rej) => {
      const req = db.transaction(_PCD_STORE, 'readonly').objectStore(_PCD_STORE).get(key);
      req.onsuccess = e => res(e.target.result ?? null);
      req.onerror   = e => rej(e.target.error);
    }));
  }

  function pcdIdbDelete(key) {
    return _pcdOpenDB().then(db => new Promise((res, rej) => {
      const tx = db.transaction(_PCD_STORE, 'readwrite');
      tx.objectStore(_PCD_STORE).delete(key);
      tx.oncomplete = res;
      tx.onerror    = e => rej(e.target.error);
    }));
  }
  // ── End IDB helpers ──────────────────────────────────────────────────────────

  function cartUrl(path) {
    return (window.Shopify?.routes?.root || '/') + path;
  }

  function dataUrlToBlob(dataUrl) {
    const [header, data] = dataUrl.split(',');
    const mime   = header.match(/:(.*?);/)[1];
    const binary = atob(data);
    const arr    = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) arr[i] = binary.charCodeAt(i);
    return new Blob([arr], { type: mime });
  }

  // ── Core upload + cart-update logic ──────────────────────────────────────────
  async function processPendingDesigns() {
    const endpoint = localStorage.getItem(ENDPOINT_KEY);
    if (!endpoint) {
      console.log(LOG, 'no upload endpoint configured — skipping');
      return;
    }

    // Fetch current cart
    let cart;
    try {
      const resp = await fetch(cartUrl('cart.js'));
      cart = await resp.json();
    } catch (e) {
      console.error(LOG, 'could not fetch cart:', e);
      return;
    }

    // Find line items that carry a _design_token
    const pendingItems = cart.items.filter(
      item => item.properties && item.properties['_design_token']
    );

    console.log(LOG, 'cart items with _design_token:', pendingItems.length);
    if (!pendingItems.length) return;

    await Promise.all(pendingItems.map(async (item) => {
      const token = item.properties['_design_token'];
      console.log(LOG, `processing token: ${token}, item key: ${item.key}`);

      // Read from IndexedDB (not localStorage — data URLs are too large)
      let designs;
      try {
        designs = await pcdIdbGet(DESIGNS_PREFIX + token);
      } catch (err) {
        console.error(LOG, 'IDB read failed:', err);
        designs = null;
      }

      if (!designs) {
        console.warn(LOG, `no design data in IDB for token "${token}"`);
        return;
      }

      console.log(LOG, 'placements to upload:', Object.keys(designs));

      // Start with existing properties, strip the token
      const newProperties = Object.assign({}, item.properties);
      delete newProperties['_design_token'];

      // Upload each placement
      await Promise.all(Object.entries(designs).map(async ([placement, design]) => {
        const propKey = `Design ${design.placementName || placement}`;

        // Prefer the mockup (product + artwork composited in the editor);
        // fall back to the raw artwork if the editor was never used.
        const uploadUrl = design.mockupUrl || design.previewUrl;

        if (!uploadUrl?.startsWith('data:')) {
          console.log(LOG, `"${placement}" already a CDN URL`);
          newProperties[propKey] = uploadUrl;
          return;
        }

        console.log(LOG, `uploading "${placement}" (${design.mockupUrl ? 'mockup' : 'artwork'})…`);
        try {
          const fd = new FormData();
          fd.append('placement',  placement);
          fd.append('product_id', design.productId || '');
          fd.append('variant_id', String(item.variant_id || ''));
          fd.append('preview',    dataUrlToBlob(uploadUrl), 'design.png');

          const resp    = await fetch(endpoint, { method: 'POST', body: fd });
          const payload = await resp.json();
          console.log(LOG, `upload response for "${placement}":`, payload);

          const cdnUrl = payload?.previewUrl || payload?.url || '';
          newProperties[propKey] = cdnUrl;

          // Update IDB entry with CDN URL so a retry skips re-upload
          if (design.mockupUrl) design.mockupUrl = cdnUrl;
          else design.previewUrl = cdnUrl;
          await pcdIdbGet(DESIGNS_PREFIX + token)
            .then(d => d && pcdIdbDelete(DESIGNS_PREFIX + token))
            .catch(() => {});
        } catch (err) {
          console.error(LOG, `upload failed for "${placement}":`, err);
        }
      }));

      console.log(LOG, 'calling /cart/change.js with:', newProperties);

      // Use item key for reliable identification (recommended by Shopify docs)
      try {
        const changeResp = await fetch(cartUrl('cart/change.js'), {
          method : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body   : JSON.stringify({
            id:         item.key,
            quantity:   item.quantity,
            properties: newProperties,
          }),
        });
        const changeBody = await changeResp.json();
        if (!changeResp.ok) {
          console.error(LOG, '/cart/change.js error:', changeResp.status, changeBody);
        } else {
          console.log(LOG, '/cart/change.js success');
          await pcdIdbDelete(DESIGNS_PREFIX + token).catch(() => {});
        }
      } catch (err) {
        console.error(LOG, '/cart/change.js request failed:', err);
      }
    }));
  }

  // ── Checkout interception ─────────────────────────────────────────────────────
  let intercepting = false;

  async function onCheckout(e) {
    // Only intercept if the customizer endpoint is configured
    if (!localStorage.getItem(ENDPOINT_KEY)) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    if (intercepting) return;
    intercepting = true;

    const btn = e.submitter
      || (e.target?.tagName === 'BUTTON' ? e.target : null)
      || (e.currentTarget?.tagName === 'BUTTON' ? e.currentTarget : null)
      || e.currentTarget?.querySelector('button[name="checkout"], button[type="submit"]');
    const origText = btn?.textContent ?? '';
    if (btn) { btn.disabled = true; btn.textContent = 'Uploading design…'; }

    console.log(LOG, 'intercepting checkout');
    try {
      await processPendingDesigns();
    } catch (err) {
      console.error(LOG, 'error during upload:', err);
    } finally {
      intercepting = false;
      if (btn) { btn.disabled = false; if (origText) btn.textContent = origText; }
    }

    console.log(LOG, 'redirecting to /checkout');
    window.location.href = '/checkout';
  }

  // ── Attach listeners ──────────────────────────────────────────────────────────
  function attachToForm(form) {
    if (form.dataset.pcdCheckoutGuard) return;
    form.dataset.pcdCheckoutGuard = 'true';
    form.addEventListener('submit', onCheckout);
    console.log(LOG, 'attached to form:', form.id || form.action);
  }

  function attachToLink(link) {
    if (link.dataset.pcdCheckoutGuard) return;
    link.dataset.pcdCheckoutGuard = 'true';
    link.addEventListener('click', onCheckout);
    console.log(LOG, 'attached to link:', link.href);
  }

  function scanAndAttach() {
    // Case 1: /checkout forms (standard cart page)
    // Case 2: checkout button IS inside its form (Dawn cart notification)
    document.querySelectorAll(
      'form[action="/checkout"], form:has(button[name="checkout"])'
    ).forEach(attachToForm);

    // Case 3: checkout button is OUTSIDE its form, linked via form="id" attribute
    // Dawn cart drawer: <button name="checkout" form="CartDrawer-Form"> (button outside form)
    // Dawn cart page:   <button name="checkout" form="cart"> (button outside form)
    document.querySelectorAll('button[name="checkout"][form]').forEach(btn => {
      const formId = btn.getAttribute('form');
      const form = document.getElementById(formId);
      if (form) attachToForm(form);
    });

    // Case 4: standalone /checkout links
    document.querySelectorAll('a[href="/checkout"]').forEach(attachToLink);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scanAndAttach);
  } else {
    scanAndAttach();
  }

  const observer = new MutationObserver(scanAndAttach);
  observer.observe(document.body, { childList: true, subtree: true });

  console.log(LOG, 'loaded');
})();
